import React, { useState, useEffect } from "react";
import axios from "axios";

const BASE = process.env.REACT_APP_SERVER_BASE_URL;
const G    = "#009444";
const RED  = "#e55";

const TAG_OPTIONS = ["ANNOUNCEMENT", "ELECTION", "NEWS", "EVENT", "ALERT"];

const fmt = (d) => d
  ? new Date(d).toLocaleDateString("en-PK", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })
  : "—";

/* ── Generic badge pill ── */
function Badge({ text, color = G }) {
  return (
    <span style={{ display: "inline-block", background: color + "22", color, border: `1px solid ${color}44`, borderRadius: 20, padding: "2px 10px", fontSize: 11, fontWeight: 700, letterSpacing: 0.3 }}>
      {text}
    </span>
  );
}

/* ── Side-drawer for website form records ── */
function DetailDrawer({ record, onClose }) {
  if (!record) return null;
  const rows = [
    ["Full Name", record.name], ["CNIC", record.cnic || "—"], ["Phone", record.phone || "—"],
    ["WhatsApp", record.whatsappNumber || "—"], ["Province", record.province || "—"],
    ["City", record.city || "—"], ["Area", record.area || "—"], ["Address", record.address || "—"],
    ["Why Join", record.whyJoin || "—"], ["Occupation", record.occupation || "—"],
    ["Contribution", record.contribution || "—"], ["Submitted", fmt(record.createdAt)],
  ];
  return (
    <>
      <div onClick={onClose} style={s.backdrop} />
      <div style={s.drawer}>
        <div style={s.drawerHead}>
          <div>
            <div style={s.drawerName}>{record.name}</div>
            <div style={s.drawerSub}>{record.province} · {record.city}</div>
          </div>
          <button onClick={onClose} style={s.closeBtn}>✕</button>
        </div>
        <div style={s.drawerBody}>
          {rows.map(([l, v]) => (
            <div key={l} style={s.drawerRow}>
              <span style={s.drawerLabel}>{l}</span>
              <span style={s.drawerVal}>{v}</span>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

/* ── Post Event modal ── */
function PostEventModal({ token, onClose, onPosted }) {
  const [title, setTitle]             = useState("");
  const [description, setDescription] = useState("");
  const [venue, setVenue]             = useState("");
  const [date, setDate]               = useState("");
  const [province, setProvince]       = useState("");
  const [district, setDistrict]       = useState("");
  const [loading, setLoading]         = useState(false);
  const [err, setErr]                 = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim() || !description.trim() || !venue.trim() || !date) {
      return setErr("Title, description, venue and date are required.");
    }
    setLoading(true); setErr("");
    try {
      await axios.post(`${BASE}/admin/events`, { title, description, venue, date, province, district }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      onPosted();
      onClose();
    } catch (ex) {
      setErr(ex.response?.data?.message || "Failed to post event.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div onClick={onClose} style={s.backdrop} />
      <div style={s.modal}>
        <div style={s.modalHead}>
          <span style={{ color: "#fff", fontWeight: 700, fontSize: 17 }}>Add Event / Rally</span>
          <button onClick={onClose} style={s.closeBtn}>✕</button>
        </div>
        <form onSubmit={submit} style={s.modalBody}>
          <label style={s.label}>Title <span style={{ color: RED }}>*</span></label>
          <input style={{ ...s.input, marginBottom: 14 }} value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g. Karachi Membership Rally" required />

          <label style={s.label}>Description <span style={{ color: RED }}>*</span></label>
          <textarea style={{ ...s.input, minHeight: 90, resize: "vertical", marginBottom: 14 }}
            value={description} onChange={e => setDescription(e.target.value)} placeholder="What members should expect" required />

          <label style={s.label}>Venue <span style={{ color: RED }}>*</span></label>
          <input style={{ ...s.input, marginBottom: 14 }} value={venue} onChange={e => setVenue(e.target.value)} placeholder="e.g. Waqar Zaka Center, Karachi" required />

          <label style={s.label}>Date & Time <span style={{ color: RED }}>*</span></label>
          <input style={{ ...s.input, marginBottom: 14 }} type="datetime-local" value={date} onChange={e => setDate(e.target.value)} required />

          <div style={{ display: "flex", gap: 14 }}>
            <div style={{ flex: 1 }}>
              <label style={s.label}>Province (optional)</label>
              <input style={{ ...s.input, marginBottom: 14 }} value={province} onChange={e => setProvince(e.target.value)} placeholder="e.g. Sindh" />
            </div>
            <div style={{ flex: 1 }}>
              <label style={s.label}>District (optional)</label>
              <input style={{ ...s.input, marginBottom: 14 }} value={district} onChange={e => setDistrict(e.target.value)} placeholder="e.g. Karachi" />
            </div>
          </div>

          {err && <p style={{ color: RED, fontSize: 12, marginTop: 8 }}>{err}</p>}

          <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
            <button type="submit" style={{ ...s.actionBtn, background: G }} disabled={loading}>
              {loading ? "Posting…" : "Post Event →"}
            </button>
            <button type="button" style={s.ghostBtn} onClick={onClose}>Cancel</button>
          </div>
        </form>
      </div>
    </>
  );
}

/* ── Post Poll modal ── */
function PostPollModal({ token, onClose, onPosted }) {
  const [question, setQuestion] = useState("");
  const [options, setOptions]   = useState(["", ""]);
  const [loading, setLoading]   = useState(false);
  const [err, setErr]           = useState("");

  const setOption = (i, val) => setOptions(prev => prev.map((o, idx) => idx === i ? val : o));
  const addOption = () => { if (options.length < 6) setOptions(prev => [...prev, ""]); };
  const removeOption = (i) => setOptions(prev => prev.filter((_, idx) => idx !== i));

  const submit = async (e) => {
    e.preventDefault();
    const clean = options.map(o => o.trim()).filter(Boolean);
    if (!question.trim() || clean.length < 2) {
      return setErr("Question and at least 2 options are required.");
    }
    setLoading(true); setErr("");
    try {
      await axios.post(`${BASE}/admin/polls`, { question, options: clean }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      onPosted();
      onClose();
    } catch (ex) {
      setErr(ex.response?.data?.message || "Failed to post poll.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div onClick={onClose} style={s.backdrop} />
      <div style={s.modal}>
        <div style={s.modalHead}>
          <span style={{ color: "#fff", fontWeight: 700, fontSize: 17 }}>Create Poll</span>
          <button onClick={onClose} style={s.closeBtn}>✕</button>
        </div>
        <form onSubmit={submit} style={s.modalBody}>
          <label style={s.label}>Question <span style={{ color: RED }}>*</span></label>
          <input style={{ ...s.input, marginBottom: 14 }} value={question} onChange={e => setQuestion(e.target.value)} placeholder="e.g. Which issue should we prioritize first?" required />

          <label style={s.label}>Options <span style={{ color: RED }}>*</span></label>
          {options.map((opt, i) => (
            <div key={i} style={{ display: "flex", gap: 8, marginBottom: 10 }}>
              <input style={s.input} value={opt} onChange={e => setOption(i, e.target.value)} placeholder={`Option ${i + 1}`} />
              {options.length > 2 && (
                <button type="button" onClick={() => removeOption(i)} style={{ ...s.ghostBtn, padding: "0 14px" }}>✕</button>
              )}
            </div>
          ))}
          {options.length < 6 && (
            <button type="button" onClick={addOption} style={{ ...s.ghostBtn, marginBottom: 14 }}>+ Add Option</button>
          )}

          {err && <p style={{ color: RED, fontSize: 12, marginTop: 8 }}>{err}</p>}

          <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
            <button type="submit" style={{ ...s.actionBtn, background: G }} disabled={loading}>
              {loading ? "Posting…" : "Post Poll →"}
            </button>
            <button type="button" style={s.ghostBtn} onClick={onClose}>Cancel</button>
          </div>
        </form>
      </div>
    </>
  );
}

/* ── Post Update modal ── */
function PostUpdateModal({ token, onClose, onPosted }) {
  const [tag, setTag]     = useState("ANNOUNCEMENT");
  const [title, setTitle] = useState("");
  const [body, setBody]   = useState("");
  const [url, setUrl]     = useState("");
  const [pinned, setPinned] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr]     = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim() || !body.trim()) return setErr("Title and body are required.");
    setLoading(true); setErr("");
    try {
      await axios.post(`${BASE}/admin/updates`, { tag, title, body, url, pinned }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      onPosted();
      onClose();
    } catch (ex) {
      setErr(ex.response?.data?.message || "Failed to post update.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div onClick={onClose} style={s.backdrop} />
      <div style={s.modal}>
        <div style={s.modalHead}>
          <span style={{ color: "#fff", fontWeight: 700, fontSize: 17 }}>Post New Update</span>
          <button onClick={onClose} style={s.closeBtn}>✕</button>
        </div>
        <form onSubmit={submit} style={s.modalBody}>
          <label style={s.label}>Tag</label>
          <select style={{ ...s.input, marginBottom: 14 }} value={tag} onChange={e => setTag(e.target.value)}>
            {TAG_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
          </select>

          <label style={s.label}>Title <span style={{ color: RED }}>*</span></label>
          <input style={{ ...s.input, marginBottom: 14 }} value={title} onChange={e => setTitle(e.target.value)} placeholder="Headline for the update" required />

          <label style={s.label}>Body <span style={{ color: RED }}>*</span></label>
          <textarea style={{ ...s.input, minHeight: 100, resize: "vertical", marginBottom: 14 }}
            value={body} onChange={e => setBody(e.target.value)} placeholder="Full message / announcement text" required />

          <label style={s.label}>Link URL (optional)</label>
          <input style={{ ...s.input, marginBottom: 14 }} value={url} onChange={e => setUrl(e.target.value)} placeholder="https://tmpak.pk/..." />

          <label style={{ ...s.label, display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }}>
            <input type="checkbox" checked={pinned} onChange={e => setPinned(e.target.checked)} style={{ width: 16, height: 16, accentColor: G }} />
            Pin to top of feed
          </label>

          {err && <p style={{ color: RED, fontSize: 12, marginTop: 8 }}>{err}</p>}

          <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
            <button type="submit" style={{ ...s.actionBtn, background: G }} disabled={loading}>
              {loading ? "Posting…" : "Post Update →"}
            </button>
            <button type="button" style={s.ghostBtn} onClick={onClose}>Cancel</button>
          </div>
        </form>
      </div>
    </>
  );
}

/* ══════════════════════════════════════════════════════════════════ */
export default function Admin() {
  const [token,      setToken]      = useState(() => localStorage.getItem("adminToken"));
  const [username,   setUsername]   = useState("");
  const [password,   setPassword]   = useState("");
  const [loginErr,   setLoginErr]   = useState("");
  const [logging,    setLogging]    = useState(false);

  const [tab,        setTab]        = useState("submissions");  // submissions | appusers | updates

  /* Submissions */
  const [subData,    setSubData]    = useState(null);
  const [subSearch,  setSubSearch]  = useState("");
  const [selected,   setSelected]   = useState(null);

  /* App users */
  const [appData,    setAppData]    = useState(null);
  const [appSearch,  setAppSearch]  = useState("");

  /* Updates */
  const [updates,    setUpdates]    = useState([]);
  const [showPost,   setShowPost]   = useState(false);
  const [deleting,   setDeleting]   = useState(null);

  /* Events */
  const [events,       setEvents]       = useState([]);
  const [showPostEvent,setShowPostEvent]= useState(false);
  const [deletingEvent,setDeletingEvent]= useState(null);

  /* Polls */
  const [polls,        setPolls]        = useState([]);
  const [showPostPoll, setShowPostPoll] = useState(false);
  const [deletingPoll, setDeletingPoll] = useState(null);
  const [togglingPoll, setTogglingPoll] = useState(null);

  const [fetchErr,   setFetchErr]   = useState("");

  useEffect(() => { if (token) { loadSubmissions(); loadAppUsers(); loadUpdates(); loadEvents(); loadPolls(); } }, [token]);

  const authH = { headers: { Authorization: `Bearer ${token}` } };

  const loadSubmissions = async () => {
    try {
      const r = await axios.get(`${BASE}/admin/submissions`, authH);
      setSubData(r.data);
    } catch { setFetchErr("Session expired. Please log in again."); handleLogout(); }
  };

  const loadAppUsers = async () => {
    try {
      const r = await axios.get(`${BASE}/admin/app-users`, authH);
      setAppData(r.data);
    } catch {}
  };

  const loadUpdates = async () => {
    try {
      const r = await axios.get(`${BASE}/admin/updates`, authH);
      setUpdates(r.data.updates || []);
    } catch {}
  };

  const handleDeleteUpdate = async (id) => {
    if (!window.confirm("Delete this update?")) return;
    setDeleting(id);
    try {
      await axios.delete(`${BASE}/admin/updates/${id}`, authH);
      setUpdates(prev => prev.filter(u => u._id !== id));
    } catch {}
    setDeleting(null);
  };

  const loadEvents = async () => {
    try {
      const r = await axios.get(`${BASE}/admin/events`, authH);
      setEvents(r.data.events || []);
    } catch {}
  };

  const handleDeleteEvent = async (id) => {
    if (!window.confirm("Delete this event?")) return;
    setDeletingEvent(id);
    try {
      await axios.delete(`${BASE}/admin/events/${id}`, authH);
      setEvents(prev => prev.filter(e => e._id !== id));
    } catch {}
    setDeletingEvent(null);
  };

  const loadPolls = async () => {
    try {
      const r = await axios.get(`${BASE}/admin/polls`, authH);
      setPolls(r.data.polls || []);
    } catch {}
  };

  const handleDeletePoll = async (id) => {
    if (!window.confirm("Delete this poll?")) return;
    setDeletingPoll(id);
    try {
      await axios.delete(`${BASE}/admin/polls/${id}`, authH);
      setPolls(prev => prev.filter(p => p._id !== id));
    } catch {}
    setDeletingPoll(null);
  };

  const handleTogglePoll = async (id) => {
    setTogglingPoll(id);
    try {
      const r = await axios.put(`${BASE}/admin/polls/${id}/close`, {}, authH);
      setPolls(prev => prev.map(p => p._id === id ? { ...p, active: r.data.active } : p));
    } catch {}
    setTogglingPoll(null);
  };

  const handleLogin = async (e) => {
    e.preventDefault(); setLoginErr(""); setLogging(true);
    try {
      const r = await axios.post(`${BASE}/admin/login`, { username, password });
      localStorage.setItem("adminToken", r.data.token);
      setToken(r.data.token);
    } catch { setLoginErr("Invalid username or password."); }
    finally { setLogging(false); }
  };

  const handleLogout = () => {
    localStorage.removeItem("adminToken");
    setToken(null); setSubData(null); setAppData(null);
  };

  /* ── Login screen ── */
  if (!token) {
    return (
      <div style={s.loginPage}>
        <div style={s.loginCard}>
          <div style={s.logo}>T</div>
          <h2 style={s.loginTitle}>Admin Portal</h2>
          <p style={{ color: "#4a7a5a", margin: "0 0 28px", fontSize: 13 }}>Technology Movement Pakistan</p>
          <form onSubmit={handleLogin}>
            <div style={s.field}><label style={s.label}>Username</label>
              <input style={s.input} type="text" value={username} onChange={e => setUsername(e.target.value)} required autoComplete="username" /></div>
            <div style={s.field}><label style={s.label}>Password</label>
              <input style={s.input} type="password" value={password} onChange={e => setPassword(e.target.value)} required autoComplete="current-password" /></div>
            {loginErr && <p style={{ color: RED, fontSize: 13, margin: "8px 0" }}>{loginErr}</p>}
            <button style={s.loginBtn} type="submit" disabled={logging}>{logging ? "Logging in…" : "Login →"}</button>
          </form>
        </div>
      </div>
    );
  }

  /* ── Filtered submissions ── */
  const allSubs  = subData?.records || [];
  const filtSubs = allSubs.filter(r => !subSearch ||
    r.name?.toLowerCase().includes(subSearch.toLowerCase()) ||
    r.cnic?.includes(subSearch) || r.phone?.includes(subSearch) ||
    r.city?.toLowerCase().includes(subSearch.toLowerCase()) ||
    r.province?.toLowerCase().includes(subSearch.toLowerCase())
  );

  /* Province breakdown */
  const provCount = allSubs.reduce((a, r) => { if (r.province) a[r.province] = (a[r.province] || 0) + 1; return a; }, {});
  const topProv   = Object.entries(provCount).sort((a, b) => b[1] - a[1]).slice(0, 4);

  /* ── Filtered app users ── */
  const allUsers  = appData?.users || [];
  const filtUsers = allUsers.filter(u => !appSearch ||
    u.name?.toLowerCase().includes(appSearch.toLowerCase()) ||
    u.email?.toLowerCase().includes(appSearch.toLowerCase()) ||
    u.phone?.includes(appSearch) ||
    u.cnic?.includes(appSearch) ||
    u.province?.toLowerCase().includes(appSearch.toLowerCase())
  );

  /* ── Dashboard ── */
  return (
    <div style={s.root}>
      {/* Top bar */}
      <div style={s.topBar}>
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <div style={s.logo}>T</div>
          <div>
            <div style={{ color: "#fff", fontWeight: 700, fontSize: 17 }}>TMP Admin Dashboard</div>
            <div style={{ color: "#4a7a5a", fontSize: 12 }}>Technology Movement Pakistan</div>
          </div>
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <button style={s.refreshBtn} onClick={() => { loadSubmissions(); loadAppUsers(); loadUpdates(); loadEvents(); loadPolls(); }}>↻ Refresh</button>
          <button style={s.ghostBtn} onClick={handleLogout}>Logout</button>
        </div>
      </div>

      {fetchErr && <p style={{ color: RED, padding: "12px 32px", fontSize: 13 }}>{fetchErr}</p>}

      {/* Tab bar */}
      <div style={s.tabBar}>
        {[
          { key: "submissions", label: `Website Members ${subData ? `(${subData.count})` : ""}` },
          { key: "appusers",   label: `App Members ${appData ? `(${appData.total})` : ""}` },
          { key: "updates",    label: `Latest Updates ${updates.length ? `(${updates.length})` : ""}` },
          { key: "events",     label: `Events & Rallies ${events.length ? `(${events.length})` : ""}` },
          { key: "polls",      label: `Polls & Surveys ${polls.length ? `(${polls.length})` : ""}` },
        ].map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            style={{ ...s.tabBtn, ...(tab === t.key ? s.tabActive : {}) }}>
            {t.label}
          </button>
        ))}
      </div>

      <div style={s.content}>

        {/* ════ SUBMISSIONS TAB ════ */}
        {tab === "submissions" && (
          <>
            <div style={s.statRow}>
              <div style={s.statCard}>
                <span style={s.statNum}>{subData?.count ?? "…"}</span>
                <span style={s.statLbl}>Total Applications</span>
              </div>
              {topProv.map(([p, c]) => (
                <div key={p} style={{ ...s.statCard, minWidth: 120 }}>
                  <span style={{ ...s.statNum, fontSize: 34 }}>{c}</span>
                  <span style={s.statLbl}>{p}</span>
                </div>
              ))}
            </div>
            <SearchBar value={subSearch} onChange={setSubSearch} placeholder="Search by name, CNIC, phone, city, province…" />
            <div style={s.tableWrap}>
              <table style={s.table}>
                <thead>
                  <tr style={s.theadRow}>
                    {["#", "Name", "CNIC", "Phone", "WhatsApp", "Province", "City", "Submitted", ""].map(h => (
                      <th key={h} style={s.th}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtSubs.map((r, i) => (
                    <tr key={r._id} style={{ ...(i % 2 === 0 ? s.trEven : s.trOdd), cursor: "pointer" }} onClick={() => setSelected(r)}>
                      <td style={{ ...s.td, color: "#555", fontSize: 12 }}>{i + 1}</td>
                      <td style={{ ...s.td, fontWeight: 600, color: "#fff" }}>{r.name}</td>
                      <td style={{ ...s.td, fontFamily: "monospace", color: "#aaa" }}>{r.cnic || "—"}</td>
                      <td style={s.td}>{r.phone || "—"}</td>
                      <td style={s.td}>{r.whatsappNumber || "—"}</td>
                      <td style={s.td}>{r.province ? <Badge text={r.province} /> : "—"}</td>
                      <td style={s.td}>{r.city || "—"}</td>
                      <td style={{ ...s.td, fontSize: 12, color: "#666" }}>{fmt(r.createdAt)}</td>
                      <td style={s.td}>
                        <button style={s.viewBtn} onClick={e => { e.stopPropagation(); setSelected(r); }}>View →</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {filtSubs.length === 0 && <p style={s.empty}>{subSearch ? `No results for "${subSearch}"` : "No applications yet."}</p>}
            </div>
            <p style={s.countNote}>Showing {filtSubs.length} of {subData?.count ?? 0}</p>
          </>
        )}

        {/* ════ APP USERS TAB ════ */}
        {tab === "appusers" && (
          <>
            <div style={s.statRow}>
              <div style={s.statCard}>
                <span style={s.statNum}>{appData?.total ?? "…"}</span>
                <span style={s.statLbl}>Total App Users</span>
              </div>
              <div style={{ ...s.statCard, minWidth: 120 }}>
                <span style={{ ...s.statNum, color: G }}>{appData?.complete ?? "…"}</span>
                <span style={s.statLbl}>Verified Members</span>
              </div>
              <div style={{ ...s.statCard, minWidth: 120 }}>
                <span style={{ ...s.statNum, color: "#e5a" }}>{appData ? appData.total - appData.complete : "…"}</span>
                <span style={s.statLbl}>Pending Profiles</span>
              </div>
            </div>
            <SearchBar value={appSearch} onChange={setAppSearch} placeholder="Search by name, email, phone, CNIC, province…" />
            <div style={s.tableWrap}>
              <table style={s.table}>
                <thead>
                  <tr style={s.theadRow}>
                    {["#", "Name", "Email", "Phone", "CNIC", "Province", "Halka", "Status", "Joined"].map(h => (
                      <th key={h} style={s.th}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtUsers.map((u, i) => (
                    <tr key={u._id} style={i % 2 === 0 ? s.trEven : s.trOdd}>
                      <td style={{ ...s.td, color: "#555", fontSize: 12 }}>{i + 1}</td>
                      <td style={{ ...s.td, fontWeight: 600, color: "#fff" }}>{u.name}</td>
                      <td style={{ ...s.td, color: "#aaa", fontSize: 12 }}>{u.email}</td>
                      <td style={s.td}>{u.phone || "—"}</td>
                      <td style={{ ...s.td, fontFamily: "monospace", color: "#aaa", fontSize: 12 }}>{u.cnic || "—"}</td>
                      <td style={s.td}>{u.province ? <Badge text={u.province} /> : "—"}</td>
                      <td style={{ ...s.td, fontSize: 12, color: "#aaa" }}>
                        {u.halka ? `${u.halka.code} · ${u.halka.name}` : "—"}
                      </td>
                      <td style={s.td}>
                        <Badge text={u.complete ? "Verified" : "Pending"} color={u.complete ? G : "#888"} />
                      </td>
                      <td style={{ ...s.td, fontSize: 12, color: "#666" }}>{fmt(u.createdAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {filtUsers.length === 0 && <p style={s.empty}>{appSearch ? `No results for "${appSearch}"` : "No app users yet."}</p>}
            </div>
            <p style={s.countNote}>Showing {filtUsers.length} of {appData?.total ?? 0}</p>
          </>
        )}

        {/* ════ UPDATES TAB ════ */}
        {tab === "updates" && (
          <>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
              <div>
                <h3 style={{ color: "#fff", margin: 0, fontSize: 18, fontWeight: 700 }}>App Latest Updates</h3>
                <p style={{ color: "#4a7a5a", fontSize: 13, margin: "4px 0 0" }}>
                  Posts appear in the app's home feed. Pinned posts show first.
                </p>
              </div>
              <button style={{ ...s.actionBtn, background: G }} onClick={() => setShowPost(true)}>
                + Post New Update
              </button>
            </div>

            {updates.length === 0
              ? <p style={s.empty}>No updates posted yet. Click "+ Post New Update" to add one.</p>
              : updates.map(u => (
                <div key={u._id} style={s.updateCard}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                        <Badge text={u.tag} color={u.tag === "ELECTION" ? "#1a6b2a" : u.tag === "NEWS" ? "#1c6ea4" : G} />
                        {u.pinned && <Badge text="📌 PINNED" color="#b87b00" />}
                        <span style={{ fontSize: 11, color: "#4a7a5a", marginLeft: "auto" }}>{fmt(u.createdAt)}</span>
                      </div>
                      <div style={{ color: "#fff", fontWeight: 700, fontSize: 15, marginBottom: 4 }}>{u.title}</div>
                      <div style={{ color: "#8aaa94", fontSize: 13, lineHeight: 1.6 }}>{u.body}</div>
                      {u.url && <a href={u.url} target="_blank" rel="noreferrer" style={{ color: G, fontSize: 12, marginTop: 6, display: "block" }}>{u.url}</a>}
                    </div>
                    <button
                      onClick={() => handleDeleteUpdate(u._id)}
                      disabled={deleting === u._id}
                      style={{ ...s.ghostBtn, color: RED, borderColor: RED + "44", marginLeft: 14, flexShrink: 0 }}>
                      {deleting === u._id ? "…" : "Delete"}
                    </button>
                  </div>
                </div>
              ))
            }
          </>
        )}

        {/* ════ EVENTS TAB ════ */}
        {tab === "events" && (
          <>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
              <div>
                <h3 style={{ color: "#fff", margin: 0, fontSize: 18, fontWeight: 700 }}>Events & Rallies</h3>
                <p style={{ color: "#4a7a5a", fontSize: 13, margin: "4px 0 0" }}>
                  Events appear in the app's Events screen. Members can RSVP.
                </p>
              </div>
              <button style={{ ...s.actionBtn, background: G }} onClick={() => setShowPostEvent(true)}>
                + Add Event
              </button>
            </div>

            {events.length === 0
              ? <p style={s.empty}>No events posted yet. Click "+ Add Event" to add one.</p>
              : events.map(ev => (
                <div key={ev._id} style={s.updateCard}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                        <Badge text={fmt(ev.date)} color="#1c6ea4" />
                        <Badge text={`${ev.rsvps?.length ?? 0} going`} />
                        <span style={{ fontSize: 11, color: "#4a7a5a", marginLeft: "auto" }}>Posted {fmt(ev.createdAt)}</span>
                      </div>
                      <div style={{ color: "#fff", fontWeight: 700, fontSize: 15, marginBottom: 4 }}>{ev.title}</div>
                      <div style={{ color: "#8aaa94", fontSize: 13, marginBottom: 4 }}>📍 {ev.venue}</div>
                      <div style={{ color: "#8aaa94", fontSize: 13, lineHeight: 1.6 }}>{ev.description}</div>
                    </div>
                    <button
                      onClick={() => handleDeleteEvent(ev._id)}
                      disabled={deletingEvent === ev._id}
                      style={{ ...s.ghostBtn, color: RED, borderColor: RED + "44", marginLeft: 14, flexShrink: 0 }}>
                      {deletingEvent === ev._id ? "…" : "Delete"}
                    </button>
                  </div>
                </div>
              ))
            }
          </>
        )}

        {/* ════ POLLS TAB ════ */}
        {tab === "polls" && (
          <>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
              <div>
                <h3 style={{ color: "#fff", margin: 0, fontSize: 18, fontWeight: 700 }}>Polls & Surveys</h3>
                <p style={{ color: "#4a7a5a", fontSize: 13, margin: "4px 0 0" }}>
                  Members vote once per poll. Close a poll to stop accepting votes.
                </p>
              </div>
              <button style={{ ...s.actionBtn, background: G }} onClick={() => setShowPostPoll(true)}>
                + Create Poll
              </button>
            </div>

            {polls.length === 0
              ? <p style={s.empty}>No polls posted yet. Click "+ Create Poll" to add one.</p>
              : polls.map(p => {
                const total = p.options.reduce((sum, o) => sum + (o.votes?.length || 0), 0);
                return (
                  <div key={p._id} style={s.updateCard}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                          <Badge text={p.active ? "ACTIVE" : "CLOSED"} color={p.active ? G : "#888"} />
                          <Badge text={`${total} vote${total === 1 ? "" : "s"}`} color="#1c6ea4" />
                          <span style={{ fontSize: 11, color: "#4a7a5a", marginLeft: "auto" }}>Posted {fmt(p.createdAt)}</span>
                        </div>
                        <div style={{ color: "#fff", fontWeight: 700, fontSize: 15, marginBottom: 10 }}>{p.question}</div>
                        {p.options.map((o, i) => {
                          const count = o.votes?.length || 0;
                          const pct = total ? Math.round((count / total) * 100) : 0;
                          return (
                            <div key={i} style={{ marginBottom: 8 }}>
                              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12.5, color: "#8aaa94", marginBottom: 3 }}>
                                <span>{o.text}</span>
                                <span>{count} ({pct}%)</span>
                              </div>
                              <div style={{ height: 6, borderRadius: 3, background: "#0a1a0f", overflow: "hidden" }}>
                                <div style={{ height: 6, borderRadius: 3, width: `${pct}%`, background: G }} />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      <div style={{ display: "flex", flexDirection: "column", gap: 8, marginLeft: 14, flexShrink: 0 }}>
                        <button
                          onClick={() => handleTogglePoll(p._id)}
                          disabled={togglingPoll === p._id}
                          style={{ ...s.ghostBtn, borderColor: G + "44", color: G }}>
                          {togglingPoll === p._id ? "…" : p.active ? "Close" : "Reopen"}
                        </button>
                        <button
                          onClick={() => handleDeletePoll(p._id)}
                          disabled={deletingPoll === p._id}
                          style={{ ...s.ghostBtn, color: RED, borderColor: RED + "44" }}>
                          {deletingPoll === p._id ? "…" : "Delete"}
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })
            }
          </>
        )}
      </div>

      {/* Detail drawer (website submissions) */}
      <DetailDrawer record={selected} onClose={() => setSelected(null)} />

      {/* Post update modal */}
      {showPost && (
        <PostUpdateModal
          token={token}
          onClose={() => setShowPost(false)}
          onPosted={loadUpdates}
        />
      )}

      {/* Post event modal */}
      {showPostEvent && (
        <PostEventModal
          token={token}
          onClose={() => setShowPostEvent(false)}
          onPosted={loadEvents}
        />
      )}

      {/* Post poll modal */}
      {showPostPoll && (
        <PostPollModal
          token={token}
          onClose={() => setShowPostPoll(false)}
          onPosted={loadPolls}
        />
      )}
    </div>
  );
}

function SearchBar({ value, onChange, placeholder }) {
  return (
    <div style={s.searchWrap}>
      <span style={{ fontSize: 16, flexShrink: 0 }}>🔍</span>
      <input style={s.searchInput} placeholder={placeholder} value={value} onChange={e => onChange(e.target.value)} />
      {value && <button style={{ background: "transparent", border: "none", color: "#555", cursor: "pointer", fontSize: 16, padding: 0 }} onClick={() => onChange("")}>✕</button>}
    </div>
  );
}

const s = {
  root:      { minHeight: "100vh", background: "#070f09", fontFamily: "system-ui,sans-serif", color: "#ccc" },
  loginPage: { minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#050e08" },
  loginCard: { background: "#0d1f12", border: `1px solid ${G}33`, borderRadius: 16, padding: "44px 40px", width: "100%", maxWidth: 380, boxShadow: `0 24px 60px rgba(0,0,0,.5)` },
  logo:      { width: 44, height: 44, borderRadius: 12, background: G, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, fontStyle: "italic", color: "#fff", fontWeight: 400, flexShrink: 0 },
  loginTitle:{ color: "#fff", margin: "16px 0 4px", fontSize: 24, fontWeight: 700 },
  field:     { marginBottom: 18 },
  label:     { display: "block", color: "#6a9a7a", fontSize: 12, fontWeight: 600, letterSpacing: 0.5, textTransform: "uppercase", marginBottom: 6 },
  input:     { width: "100%", padding: "11px 14px", background: "rgba(255,255,255,.05)", border: "1.5px solid rgba(255,255,255,.1)", borderRadius: 10, color: "#fff", fontSize: 14, boxSizing: "border-box", outline: "none" },
  loginBtn:  { width: "100%", padding: 13, background: G, color: "#fff", border: "none", borderRadius: 10, fontSize: 15, fontWeight: 700, cursor: "pointer", marginTop: 8 },

  topBar:    { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 32px", background: "#0d1f12", borderBottom: `1px solid ${G}33`, position: "sticky", top: 0, zIndex: 50 },
  refreshBtn:{ background: "transparent", border: `1px solid ${G}55`, color: G, padding: "7px 16px", borderRadius: 8, cursor: "pointer", fontSize: 13, fontWeight: 600 },
  ghostBtn:  { background: "transparent", border: "1px solid #333", color: "#777", padding: "7px 16px", borderRadius: 8, cursor: "pointer", fontSize: 13 },
  actionBtn: { border: "none", color: "#fff", padding: "9px 20px", borderRadius: 9, cursor: "pointer", fontSize: 13, fontWeight: 700 },

  tabBar:    { display: "flex", gap: 2, padding: "0 32px", background: "#0a170d", borderBottom: `1px solid ${G}22` },
  tabBtn:    { padding: "13px 18px", background: "transparent", border: "none", color: "#4a7a5a", cursor: "pointer", fontSize: 14, fontWeight: 600, borderBottom: "2px solid transparent" },
  tabActive: { color: G, borderBottom: `2px solid ${G}` },

  content:   { padding: "28px 32px" },
  statRow:   { display: "flex", gap: 16, flexWrap: "wrap", marginBottom: 28 },
  statCard:  { background: "#0d1f12", border: `1px solid ${G}33`, borderRadius: 14, padding: "22px 28px", display: "flex", flexDirection: "column", minWidth: 160 },
  statNum:   { color: G, fontSize: 44, fontWeight: 800, lineHeight: 1 },
  statLbl:   { color: "#4a7a5a", fontSize: 13, marginTop: 6 },

  searchWrap:  { display: "flex", alignItems: "center", gap: 10, background: "#0d1f12", border: `1.5px solid ${G}33`, borderRadius: 12, padding: "10px 16px", marginBottom: 20 },
  searchInput: { flex: 1, background: "transparent", border: "none", outline: "none", color: "#fff", fontSize: 14 },

  tableWrap: { overflowX: "auto", background: "#0d1f12", border: `1px solid ${G}22`, borderRadius: 14, marginBottom: 8 },
  table:     { width: "100%", borderCollapse: "collapse", minWidth: 900 },
  theadRow:  { background: "#0a1a0f" },
  th:        { color: "#3a6a4a", fontSize: 11, textTransform: "uppercase", letterSpacing: 0.8, padding: "12px 14px", textAlign: "left", borderBottom: `1px solid ${G}22`, fontWeight: 700 },
  td:        { color: "#ccc", fontSize: 13.5, padding: "12px 14px", borderBottom: "1px solid #0f1f14" },
  trEven:    { background: "#0d1f12" },
  trOdd:     { background: "#0a1a0f" },
  viewBtn:   { background: `${G}22`, border: `1px solid ${G}44`, color: G, padding: "5px 12px", borderRadius: 6, cursor: "pointer", fontSize: 12, fontWeight: 700 },
  countNote: { color: "#3a5a44", fontSize: 12, textAlign: "right" },
  empty:     { color: "#3a5a44", textAlign: "center", padding: "48px 32px", fontSize: 14 },

  updateCard:{ background: "#0d1f12", border: `1px solid ${G}22`, borderRadius: 14, padding: "18px 20px", marginBottom: 12 },

  /* Drawer */
  backdrop:  { position: "fixed", inset: 0, background: "rgba(0,0,0,.55)", zIndex: 200, backdropFilter: "blur(4px)" },
  drawer:    { position: "fixed", top: 0, right: 0, bottom: 0, width: "min(420px,94vw)", background: "#0d1f12", borderLeft: `1px solid ${G}33`, zIndex: 201, display: "flex", flexDirection: "column", boxShadow: "-16px 0 48px rgba(0,0,0,.4)" },
  drawerHead:{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", padding: "24px 24px 18px", background: `linear-gradient(135deg,${G}22 0%,transparent 100%)`, borderBottom: `1px solid ${G}22` },
  drawerName:{ fontSize: 19, fontWeight: 700, color: "#fff", marginBottom: 4 },
  drawerSub: { fontSize: 13, color: "#4a7a5a" },
  drawerBody:{ flex: 1, overflowY: "auto", padding: "20px 24px" },
  drawerRow: { display: "flex", flexDirection: "column", gap: 4, padding: "14px 0", borderBottom: "1px solid #0f1f14" },
  drawerLabel:{ fontSize: 10.5, fontWeight: 700, letterSpacing: 0.8, textTransform: "uppercase", color: "#3a6a4a" },
  drawerVal: { fontSize: 14, color: "#d0e8d8", lineHeight: 1.6 },
  closeBtn:  { background: "rgba(255,255,255,.07)", border: "1px solid rgba(255,255,255,.12)", color: "#aaa", width: 34, height: 34, borderRadius: "50%", cursor: "pointer", fontSize: 14, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 },

  /* Modal */
  modal:     { position: "fixed", top: "50%", left: "50%", transform: "translate(-50%,-50%)", width: "min(520px,95vw)", background: "#0d1f12", border: `1px solid ${G}33`, borderRadius: 18, zIndex: 201, boxShadow: "0 32px 80px rgba(0,0,0,.6)", maxHeight: "90vh", display: "flex", flexDirection: "column" },
  modalHead: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "20px 24px 16px", borderBottom: `1px solid ${G}22` },
  modalBody: { padding: "20px 24px 24px", overflowY: "auto" },
};
